/* eslint-disable react/button-has-type */
/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/**
 * Copyright (c) 2021, Ouster, Inc.
 * All rights reserved.
 */
import {
  DownloadMenu,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  ModalContainer,
  ShareWithOusterModal,
  Shortcut,
  ShortcutAddonKey,
  ShortcutCategory,
  ShortcutKey,
  ShortcutsModal,
  UnshareWithOusterAlert,
  useDisclosure,
  VizTitle,
  AccessLevel,
  Button,
} from '@ouster/webviz-react-components';

import { useQueryClient } from '@tanstack/react-query';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { isMacOs } from 'react-device-detect';
import { TbEyeStar } from 'react-icons/tb';
import { useNavigate, useParams } from 'react-router-dom';

import {
  NavBar,
  NavFullHeightContainer,
  NavFlexRow,
} from '../containers/NavBar';
import useChakraHook from '../hooks/useChakraHook';
import useIsMobileViewport from '../hooks/useIsMobileViewport';
import { useStoreState, useStoreActions } from '../reducers';
import {
  FileInfo,
  OutputFileInfo,
  RecordingPublicityAccessConfig,
} from '../types';
import { BASE_ORG_API, BASE_PUBLISHED_RECORDING_API } from '../utils/constants';
import { useFeedbackForm } from '../utils/customHooks';
import { fetchWithAuth } from '../utils/requests';
import {
  canCopyOusterRecording,
  isAdminLevelRole,
  isOusterSuperAdminRole,
} from '../utils/roles';
import NavPublish from './navBar/NavPublish';
import { PublicDownloadWarning } from './publicSample/PublicDownloadWarning';
import CopyRecordingToOrganizations from './recordingTable/CopyRecordingToOrganizations';
import NavUser from './users/NavUser';
import { driveSensorInfoArrayToRaw } from '../utils/driveUtil';
import RecordingAccessConfig from './recordingTable/RecordingAccessConfig';
import { buildTalkToSalesLink } from '../utils/talkToSales';

const ACTIONS = [
  'DOWNLOAD-RECORDING',
  'SHARE-RECORDING',
  'UNSHARE-RECORDING',
  'DELETE-RECORDING',
  'PUBLISH-RECORDING',
  'UNPUBLISH-RECORDING',
  'COPY-RECORDING-LINK',
  'COPY-TO-ORGANIZATIONS',
  'SAMPLE-CONFIGURE',
  'SEND-FEEDBACK',
  'KEYBOARD-SHORTCUTS',
] as const;

type TMenuItem = {
  label: string;
  action: typeof ACTIONS[number];
  icon: React.ReactElement;
};

/// ///////////////////////////////////////////////////////////////////////////
// Final NavBar
/// ///////////////////////////////////////////////////////////////////////////

const DriveNavBar: React.FC<{ orgId: number }> = ({ orgId }) => {
  const { isPublished, driveInfo, isShared, publicationInfo } = useStoreState(
    (store) => ({
      ...store.drive,
    })
  );
  const { curUserOrg, appUser, user } = useStoreState((store) => ({
    ...store.user,
  }));
  const { deleteDrive, shareWithOuster, loadNewContentStatus } =
    useStoreActions((actions) => actions.drive);
  const { openConfirmDialog } = useStoreActions(
    (storeState) => storeState.confirmDialog
  );
  const { shortcuts } = useStoreState((store) => ({
    ...store.shortcut,
  }));

  const isSampleRecording =
    driveInfo?.accessLevel?.includes('PUBLIC_SAMPLE') ?? false;
  const hasKcToken = Boolean(user?.identity_id || appUser?.identity_id);

  const queryClient = useQueryClient();

  const {
    sourceFileInfo,
    outputForDownload,
    outputForDownloadNonSlam,
    sourceDataForDownload,
    sourceConfigForDownload,
  } = useStoreState((store) => ({ ...store.drive }));
  const { addShortcut } = useStoreActions((actions) => actions.shortcut);

  const [sharingRevealed, setSharingRevealed] = useState(false);
  const [downloadRevealed, setDownloadRevealed] = useState(false);
  const [shortcutsRevealed, setShortcutsRevealed] = useState(false);

  const {
    isOpen: isShareModalOpen,
    onOpen: onShareModalOpen,
    onClose: onShareModalClose,
  } = useDisclosure();

  const {
    isOpen: isUnshareAlertOpen,
    onOpen: onUnshareAlertOpen,
    onClose: onUnshareAlertClose,
  } = useDisclosure();

  const {
    isOpen: isDownloadWarningModalOpen,
    onOpen: onDownloadWarningModalOpen,
    onClose: onDownloadWarningModalClose,
  } = useDisclosure();

  const {
    isOpen: isCopyToOrganizationsOpen,
    onOpen: onCopyToOrganizationsOpen,
    onClose: onCopyToOrganizationsClose,
  } = useDisclosure();

  const { showToast } = useChakraHook();
  const navigate = useNavigate();
  const { publishedDriveUuid: publishedDriveUuidRouteParam } =
    useParams<{ publishedDriveUuid: string }>();
  const [downloadMenuPosition, setDownloadMenuPosition] = useState({
    top: 0,
    right: 0,
  });
  const downloadButtonRef = useRef<HTMLButtonElement>(null);
  const isMobileViewport = useIsMobileViewport();

  useEffect(() => {
    addShortcut(
      new Shortcut(
        ShortcutCategory.Other,
        [
          // I think this is more usable but we should see how it works on
          // international keyboards like AZERTY
          new ShortcutKey('?', [ShortcutAddonKey.SHIFT], '/'),
          new ShortcutKey('/', [ShortcutAddonKey.CTRL]),
        ],
        () => {
          setShortcutsRevealed((wasRevealed) => !wasRevealed);
        },
        'Toggle Shortcuts'
      )
    );
  }, [addShortcut]);

  /* 
    Control for recording publicity configuration modal
  */
  const {
    isOpen: isPublicitySettingOpen,
    onOpen: onPublicitySettingOpen,
    onClose: onPublicitySettingClose,
  } = useDisclosure();
  const publicityRecordingRef = useRef<
    RecordingPublicityAccessConfig | undefined
  >(undefined);

  const onConfigureSamples = () => {
    if (!driveInfo) return;
    publicityRecordingRef.current = {
      organization_id: driveInfo.organizationId,
      id: Number(driveInfo.id),
      publication_uuid: publicationInfo?.uuid,
      data_category: driveInfo.dataCategory,
      sensor_info: driveSensorInfoArrayToRaw(driveInfo.sensorInfos),
      start_time: driveInfo.startTime === '...' ? null : driveInfo.startTime,
      recording_title: driveInfo.recordingTitle,
    };

    onPublicitySettingOpen();
  };

  const onPublicitySettingCloseHandler = useCallback(() => {
    publicityRecordingRef.current = undefined;
    if (localStorage.getItem('recordingAccessConfigChanged')) {
      queryClient.invalidateQueries({ queryKey: ['shared-from-list'] });
      queryClient.invalidateQueries({ queryKey: ['drive-list'] });

      localStorage.removeItem('recordingAccessConfigChanged');
    }

    onPublicitySettingClose();
  }, [onPublicitySettingClose, queryClient]);

  const onDeleteDrive = () => {
    if (!driveInfo) return;

    const onConfirm = async () => {
      try {
        await deleteDrive({ orgId, driveId: driveInfo.id });

        if (isShared) {
          // If the drive is shared, we need to invalidate the shared list
          // to remove the deleted drive from the list
          queryClient.invalidateQueries({ queryKey: ['shared-with-list'] });
        }
        // when an ouster published drive is deleted. invalidate sample data query
        if (
          driveInfo.accessLevel !== AccessLevel.NONE &&
          curUserOrg?.id === 1
        ) {
          queryClient.invalidateQueries({
            queryKey: ['public-public-drive-list'],
          });
          queryClient.invalidateQueries({
            queryKey: ['private-public-drive-list'],
          });

          loadNewContentStatus({ organizationId: curUserOrg.id });
        }

        showToast({
          title: 'Recording deleted',
          description: `Recording deleted, redirecting to recordings page in 3 seconds`,
          status: 'info',
        });
        setTimeout(() => {
          navigate('/drive/private');
        }, 2000);
      } catch (error) {
        showToast({
          title: 'Failed to delete recording',
          description: `${error}`,
          status: 'error',
        });
      }
    };

    // Build bullets at call site so we can include context-specific warnings
    const bullets = [
      'This recording will be permanently deleted and cannot be recovered.',
    ];
    if (isPublished) {
      bullets.push(
        'This recording is published and will be removed from public listings.'
      );
    }
    if (isShared) {
      bullets.push(
        'This recording is shared with Ouster. Deleting it will remove access for the Ouster support team.'
      );
    }

    openConfirmDialog({
      title: 'Delete recording',
      body: 'Are you sure you want to delete this recording?',
      bullets,
      onConfirm,
      isDestructive: true,
      confirmLabel: 'Delete recording',
      cancelLabel: 'Cancel',
    });
  };

  const feedbackFormLink = useFeedbackForm();
  const onOpenFeedbackForm = () => {
    const newWindow = window.open(
      feedbackFormLink,
      '_blank',
      'noopener,noreferrer'
    );
    if (newWindow) {
      newWindow.opener = null;
    }
  };

  const handleDownloadRequest = () => {
    if (hasKcToken || !isSampleRecording) {
      if (downloadButtonRef.current) {
        const buttonRect = downloadButtonRef.current.getBoundingClientRect();
        const scrollY = window.scrollY || document.documentElement.scrollTop;

        setDownloadMenuPosition({
          top: buttonRect.bottom + scrollY + 8,
          right: window.innerWidth - buttonRect.right,
        });
      }

      setDownloadRevealed(true);
    } else {
      onDownloadWarningModalOpen();
    }
  };

  const getFileDownloadUrl = useCallback(
    async (
      driveUuid: string,
      file: FileInfo | OutputFileInfo,
      type: string,
      downloadFilename?: string
    ) => {
      const downloadPath = publishedDriveUuidRouteParam
        ? `${BASE_PUBLISHED_RECORDING_API}/${driveUuid}/${type}/files/${file.id}/signed_url`
        : `${BASE_ORG_API}/${orgId}/recordings/${driveUuid}/${type}/files/${file.id}/signed_url`;

      const response = await fetchWithAuth(downloadPath, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          downloadFilename: file.filename ?? downloadFilename,
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Something went wrong when creating signed URL for file ${file.id}`
        );
      }

      return response.json() as Promise<{ url: string }>;
    },
    [orgId, publishedDriveUuidRouteParam]
  );

  const getMenuItems = (): TMenuItem[] => {
    const items: TMenuItem[] = [];
    const canCopyRecording = canCopyOusterRecording(
      curUserOrg?.id,
      curUserOrg?.role,
      driveInfo?.organizationId
    );

    items.push({
      label: 'Download Recording',
      action: 'DOWNLOAD-RECORDING',
      icon: <Icon icon="download" />,
    });
    items.push({
      label: 'Copy the visualization link',
      action: 'COPY-RECORDING-LINK',
      icon: <Icon icon="share" />,
    });
    if (canCopyRecording) {
      items.push({
        label: 'Copy to Organization(s)',
        action: 'COPY-TO-ORGANIZATIONS',
        icon: <Icon icon="contentCopy" />,
      });
    }

    // Items available only if the drive belongs to the current user's organization
    if (driveInfo?.organizationId === curUserOrg?.id) {
      // Ouster user don't need to share button
      if (curUserOrg?.id !== 1) {
        items.push({
          label: isShared
            ? 'Stop Sharing with Ouster'
            : 'Share Recording with Ouster',
          action: isShared ? 'UNSHARE-RECORDING' : 'SHARE-RECORDING',
          icon: <Icon icon={isShared ? 'cancelSend' : 'send'} />,
        });
      }
      // Items available if the user is the uploader or an admin
      if (
        appUser?.id === driveInfo?.uploadedById ||
        isAdminLevelRole(curUserOrg?.role)
      ) {
        items.push({
          label: 'Delete Recording',
          action: 'DELETE-RECORDING',
          icon: <Icon icon="delete" />,
        });
      }

      if (isOusterSuperAdminRole(curUserOrg?.id, curUserOrg?.role)) {
        // Ouster user can configure sample data
        items.push({
          label: 'Configure Sample Access',
          action: 'SAMPLE-CONFIGURE',
          icon: <TbEyeStar size={24} />,
        });
      }
    }

    // Publish/unpublish option for any logged-in user and user can only do that within their own organization
    if (
      curUserOrg &&
      curUserOrg.id === driveInfo?.organizationId &&
      isAdminLevelRole(curUserOrg?.role)
    ) {
      if (!isPublished && driveInfo?.driveOrigin !== 'SAVE') {
        items.push({
          label: 'Publish Recording',
          action: 'PUBLISH-RECORDING',
          icon: <Icon icon="public" />,
        });
      }
      // only the admin or the one who published can unpublish it
      if (isPublished) {
        items.push({
          label: 'Manage Published Recording',
          action: 'UNPUBLISH-RECORDING',
          icon: <Icon icon="cloudSync" />,
        });
      }
    }

    // Additional common actions
    items.push(
      {
        label: 'Send Feedback',
        action: 'SEND-FEEDBACK',
        icon: <Icon icon="feedback" />,
      },
      {
        label: 'Keyboard Shortcuts',
        action: 'KEYBOARD-SHORTCUTS',
        icon: <Icon icon="keyboardAlt" />,
      }
    );

    return items;
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast({
        title: '',
        description: 'Link copied to clipboard',
        status: 'success',
      });
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const copyCloudVizLink = () => {
    const isPublic = publishedDriveUuidRouteParam;
    const fullUrl = isPublic
      ? `${window.location.origin}/share/${publishedDriveUuidRouteParam}`
      : `${window.location.origin}/organization/${driveInfo?.organizationId}/drive/${driveInfo?.id}`;
    copyToClipboard(fullUrl);
  };

  const handleMenuClick = (item: TMenuItem) => {
    switch (item.action) {
      case 'DOWNLOAD-RECORDING':
        handleDownloadRequest();
        break;
      case 'SHARE-RECORDING':
        onShareModalOpen();
        break;
      case 'UNSHARE-RECORDING':
        onUnshareAlertOpen();
        break;
      case 'DELETE-RECORDING':
        onDeleteDrive();
        break;
      case 'PUBLISH-RECORDING':
      case 'UNPUBLISH-RECORDING':
        setSharingRevealed(true);
        break;
      case 'COPY-RECORDING-LINK':
        copyCloudVizLink();
        break;
      case 'COPY-TO-ORGANIZATIONS':
        onCopyToOrganizationsOpen();
        break;
      case 'SAMPLE-CONFIGURE':
        onConfigureSamples();
        break;
      case 'SEND-FEEDBACK':
        onOpenFeedbackForm();
        break;
      case 'KEYBOARD-SHORTCUTS':
        setShortcutsRevealed(true);
        break;
      default:
        console.warn(`Unsupported action: ${item.action}`);
        break;
    }
  };

  const {
    isOpen: isMenuOpen,
    onClose: onMenuClose,
    onOpen: onMenuOpen,
  } = useDisclosure();

  const onShareRecording = async (notes?: string) => {
    if (!curUserOrg?.id || !driveInfo?.id) return;
    try {
      await shareWithOuster({
        organizationId: curUserOrg.id,
        driveId: driveInfo?.id,
        shareWithOuster: true,
        notes,
      });
      showToast({
        title: 'Recording shared',
        description: `Recording has been successfully shared with Ouster`,
        status: 'info',
      });
      queryClient.invalidateQueries({ queryKey: ['shared-with-list'] });
    } catch (error) {
      showToast({
        title: 'Failed to share recording',
        description: `${error}`,
        status: 'error',
      });
    }
  };

  const onUnshareConfirm = () => {
    const unshare = async () => {
      if (!curUserOrg?.id || !driveInfo?.id) return;

      try {
        await shareWithOuster({
          organizationId: curUserOrg.id,
          driveId: driveInfo?.id,
          shareWithOuster: false,
        });
        onUnshareAlertClose();
        showToast({
          title: 'Recording unshared',
          description: `Recording has been unshared with Ouster`,
          status: 'info',
        });
        queryClient.invalidateQueries({ queryKey: ['shared-with-list'] });
      } catch (error) {
        showToast({
          title: 'Failed to unshared recording',
          description: `${error}`,
          status: 'error',
        });
      }
    };
    unshare();
  };

  const vizTitleProps: {
    text?: string;
    recordingTitle?: string;
    details?: string;
  } = {};
  if (driveInfo) {
    const sensorInfos = driveInfo.sensorInfos || [];
    const sensorInfo = sensorInfos[0];
    const isMultisensor = sensorInfos.length > 1;
    const details: string[] = [];
    if (isMultisensor) {
      details.push('Multi-sensor');
    } else if (sensorInfo) {
      details.push(
        `${sensorInfo.sdkProductType} - ${sensorInfo.sdkProductResolution} | ${sensorInfo.sdkFwVersionCore} | Rev ${sensorInfo.sdkProductHwRevision} | ${sensorInfo.sdkLidarMode}`
      );
    }
    if (driveInfo.uploadedBy) {
      details.push(`by ${driveInfo.uploadedBy}`);
    }
    if (isShared) {
      details.push('Shared with Ouster');
    }
    const recTitle = driveInfo.recordingTitle || undefined;
    const detailsText = details.filter(Boolean).join(' | ');
    if (!recTitle) {
      const titleSegments: string[] = [];
      if (isMultisensor) {
        titleSegments.push('Multi-sensor');
      } else if (sensorInfo) {
        titleSegments.push(
          `${sensorInfo.sdkProductType} - ${sensorInfo.sdkProductResolution} | ${sensorInfo.sdkFwVersionCore} | Rev ${sensorInfo.sdkProductHwRevision} | ${sensorInfo.sdkLidarMode}`
        );
      }
      if (driveInfo.uploadedBy) {
        titleSegments.push(`by ${driveInfo.uploadedBy}`);
      }
      if (isShared) {
        titleSegments.push('Shared with Ouster');
      }
      const titleText = titleSegments.filter(Boolean).join(' | ');
      vizTitleProps.text = titleText;
    } else {
      vizTitleProps.recordingTitle = recTitle;
      vizTitleProps.details = detailsText;
    }
  }

  const talkToSalesLink = buildTalkToSalesLink(
    driveInfo?.recordingTitle || vizTitleProps.text
  );

  return (
    <>
      <NavBar
        data-testid="drive-nav-bar-root"
        centerElement={
          driveInfo && (
            <NavFullHeightContainer
              style={{
                overflow: 'hidden',
                placeItems: 'unset',
                alignItems: 'center',
              }}
            >
              {'recordingTitle' in vizTitleProps ? (
                <VizTitle
                  recordingTitle={vizTitleProps.recordingTitle}
                  details={isMobileViewport ? undefined : vizTitleProps.details}
                />
              ) : (
                <VizTitle text={vizTitleProps.text} />
              )}
            </NavFullHeightContainer>
          )
        }
        rightElement={
          <NavFullHeightContainer>
            <NavFlexRow>
              {isSampleRecording && (
                <Button
                  variant="outline"
                  onClick={() => {
                    const newWindow = window.open(
                      talkToSalesLink,
                      '_blank',
                      'noopener,noreferrer'
                    );
                    if (newWindow) {
                      newWindow.opener = null;
                    }
                  }}
                  data-testid="drive-nav-bar-talk-to-sales-btn"
                >
                  Talk to Sales
                </Button>
              )}
              {!isMobileViewport && (
                <>
                  <IconButton
                    ref={downloadButtonRef}
                    emphasis="subtle"
                    variant="solid"
                    icon="download"
                    onClick={handleDownloadRequest}
                    data-testid="drive-nav-bar-download-btn"
                  />

                  <Menu
                    placement="top-end"
                    offset={[5, 10]}
                    autoSelect={false}
                    onClose={onMenuClose}
                    isOpen={isMenuOpen}
                    data-testid="drive-nav-bar-settings-menu"
                  >
                    <MenuButton
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                      as={IconButton}
                      emphasis="subtle"
                      icon="settings"
                      color="white"
                      onClick={onMenuOpen}
                      data-testid="drive-nav-bar-settings-btn"
                    />
                    <MenuList data-testid="drive-nav-bar-menu-list">
                      {getMenuItems().map((item) => (
                        <MenuItem
                          key={item.action}
                          onClick={() => handleMenuClick(item)}
                          style={{
                            fontSize: 14,
                            paddingBlock: '0.75rem',
                            paddingInline: '1.25rem',
                            marginBlock: '0.25rem',
                            columnGap: '0.5rem',
                          }}
                          _hover={{ bgColor: 'var(--color-blast-off-blue)' }}
                          _focus={{ bgColor: 'var(--color-blast-off-blue)' }}
                          data-testid={`drive-nav-bar-menu-item-${item.action.toLowerCase()}`}
                        >
                          <span style={{ fontSize: 20 }}>{item.icon}</span>
                          {item.label}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                </>
              )}

              {curUserOrg?.id && <NavUser data-testid="drive-nav-bar-user" />}
            </NavFlexRow>
          </NavFullHeightContainer>
        }
      />
      <DownloadMenu
        driveUuid={publishedDriveUuidRouteParam ?? String(driveInfo?.id ?? '')}
        revealed={downloadRevealed}
        sourceDataForDownload={sourceDataForDownload}
        sourceConfigForDownload={sourceConfigForDownload}
        downloadableOutputFiles={
          outputForDownload.length > 0
            ? outputForDownload
            : outputForDownloadNonSlam
        }
        getFileDownloadUrl={getFileDownloadUrl}
        onClickedOutside={() => {
          if (downloadRevealed) {
            setDownloadRevealed(false);
          }
        }}
        recordingTitle={driveInfo?.recordingTitle || undefined}
        popupProps={{
          style: {
            top: downloadMenuPosition.top,
            right: downloadMenuPosition.right,
          },
          'data-testid': 'drive-nav-bar-download-menu',
        }}
      />
      <NavPublish
        revealed={sharingRevealed}
        sourceFilesInfo={sourceFileInfo}
        onClickedOutside={() => {
          if (sharingRevealed) {
            setSharingRevealed(false);
          }
        }}
      />
      <ShortcutsModal
        isOpen={shortcutsRevealed}
        onClose={() => {
          if (shortcutsRevealed) {
            setShortcutsRevealed(false);
          }
        }}
        isMac={isMacOs}
        shortcuts={shortcuts}
      />
      {isShareModalOpen && (
        <ShareWithOusterModal
          isOpen={isShareModalOpen}
          handleClose={onShareModalClose}
          handleShare={onShareRecording}
        />
      )}

      <UnshareWithOusterAlert
        isUnshareAlertOpen={isUnshareAlertOpen}
        onUnshareAlertClose={onUnshareAlertClose}
        onUnshareConfirm={onUnshareConfirm}
      />

      <ModalContainer
        isOpen={isDownloadWarningModalOpen}
        onClose={onDownloadWarningModalClose}
        header="Unlock Sample lidar Downloads"
        footer={null}
        content={<PublicDownloadWarning />}
        data-testid="public-collection-download-warning-modal"
      />

      {driveInfo && (
        <ModalContainer
          isOpen={isCopyToOrganizationsOpen}
          onClose={onCopyToOrganizationsClose}
          header="Copy to Organization(s)"
          footer={null}
          content={
            <CopyRecordingToOrganizations
              driveId={driveInfo.id}
              organizationId={driveInfo.organizationId}
              recordingTitle={driveInfo.recordingTitle}
              onClose={onCopyToOrganizationsClose}
            />
          }
          data-testid="drive-nav-bar-copy-to-organizations-modal"
        />
      )}

      {publicityRecordingRef.current && (
        <ModalContainer
          isOpen={isPublicitySettingOpen}
          onClose={onPublicitySettingCloseHandler}
          header="Recording Access Configuration"
          footer={null}
          content={
            <RecordingAccessConfig
              recording={publicityRecordingRef.current}
              onClose={onPublicitySettingCloseHandler}
            />
          }
        />
      )}
    </>
  );
};

export { DriveNavBar };
